import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { FiSave, FiLock, FiLogOut, FiPhone, FiMapPin, FiCalendar, FiX } from "react-icons/fi";
import CustomInput from "./CustomInput/CustomInput";
import apiCall from "../../Api/apiCall";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/features/authSlice";

import EmptyModal from "./EmptyModal";

const UserProfileModal = ({ show, onHide, user }) => {
   const [loading, setLoading] = useState(false);
   const [showPasswordChange, setShowPasswordChange] = useState(false);
   const [showConfirmModal, setShowConfirmModal] = useState(false);
   const [pendingPasswordData, setPendingPasswordData] = useState(null);
   const dispatch = useDispatch();

   const passwordSchema = yup.object().shape({
      password: yup.string().min(6, "Minimum 6 characters").required("Required"),
      password_confirmation: yup.string().oneOf([yup.ref("password"), null], "Passwords must match").required("Required"),
   });

   const formik = useFormik({
      initialValues: {
         password: "",
         password_confirmation: "",
      },
      validationSchema: passwordSchema,
      onSubmit: async (values) => {
         setPendingPasswordData({
            password: values.password,
            password_confirmation: values.password_confirmation,
         });
         setShowConfirmModal(true);
      },
   });

   const executePasswordChange = async () => {
      if (!pendingPasswordData) return;
      
      setShowConfirmModal(false);
      await apiCall({
         method: "PATCH",
         endpoint: `/user/${user?.id}`,
         data: pendingPasswordData,
         setLoading,
         successMsg: "Password updated successfully. Please login again.",
         onSuccess: () => {
            formik.resetForm();
            setShowPasswordChange(false);
            setPendingPasswordData(null);
            
            setTimeout(() => {
               dispatch(logout());
            }, 1000);
         },
      });
   };

   useEffect(() => {
      if (show) {
         formik.resetForm();
         setShowPasswordChange(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [show]);

   if (!show || !user) return null;

   const roleName = user?.roles?.[0]?.name || "User";

   return (
      <div style={{
         position: "fixed",
         top: 0, left: 0, right: 0, bottom: 0,
         zIndex: 9999,
         fontFamily: "'Inter', sans-serif"
      }} onClick={onHide}>
         <div 
            onClick={(e) => e.stopPropagation()}
            style={{
            background: "#fff",
            borderRadius: "24px",
            width: "360px",
            position: "absolute",
            top: "85px",
            right: "20px",
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            animation: "fadeInDown 0.2s ease-out"
         }}>
            {/* Close Button */}
            <button 
               type="button"
               onClick={onHide}
               style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  cursor: "pointer",
                  zIndex: 10,
                  backdropFilter: "blur(2px)",
                  transition: "background 0.2s"
               }}
               onMouseOver={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.4)"}
               onMouseOut={(e) => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
            >
               <FiX size={18} />
            </button>

            {/* Header / Cover */}
            <div style={{
               height: "120px",
               background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
               position: "relative"
            }}>
               <div style={{
                  position: "absolute",
                  bottom: "-40px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "88px",
                  height: "88px",
                  borderRadius: "50%",
                  background: "#4f46e5",
                  border: "4px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2.5rem",
                  color: "#fff",
                  fontWeight: "bold",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.12)"
               }}>
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
               </div>
            </div>

            {/* Profile Info */}
            <div style={{ padding: "50px 24px 24px", textAlign: "center" }}>
               <h4 style={{ margin: "0 0 4px", fontSize: "1.4rem", fontWeight: "700", color: "#1e293b" }}>
                  {user?.name}
               </h4>
               <p style={{ margin: "0 0 20px", fontSize: "0.85rem", color: "#64748b", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {roleName}
               </p>

               {!showPasswordChange ? (
                  <>
                     {/* Info Items */}
                     <div style={{
                        background: "#f8fafc",
                        borderRadius: "16px",
                        padding: "16px",
                        textAlign: "left",
                        marginBottom: "24px",
                        border: "1px solid #f1f5f9"
                     }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                           <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", color: "#4f46e5" }}>
                              <FiPhone size={16} />
                           </div>
                           <span style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "500" }}>{user?.mobile || "Not provided"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                           <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", color: "#4f46e5" }}>
                              <FiMapPin size={16} />
                           </div>
                           <span style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "500" }}>{user?.address || "Not provided"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                           <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", color: "#4f46e5" }}>
                              <FiCalendar size={16} />
                           </div>
                           <span style={{ fontSize: "0.95rem", color: "#334155", fontWeight: "500" }}>
                              Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "N/A"}
                           </span>
                        </div>
                     </div>

                     {/* Action Buttons */}
                     <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                        <button
                           type="button"
                           onClick={() => setShowPasswordChange(true)}
                           style={{
                              flex: 1,
                              padding: "12px 0",
                              border: "1px solid #e2e8f0",
                              background: "#fff",
                              borderRadius: "12px",
                              color: "#475569",
                              fontWeight: "600",
                              fontSize: "0.9rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
                           }}
                           onMouseOver={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                           onMouseOut={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
                        >
                           <FiLock style={{ marginRight: "8px" }} /> Password
                        </button>
                        <button
                           type="button"
                           onClick={() => dispatch(logout())}
                           style={{
                              flex: 1,
                              padding: "12px 0",
                              border: "none",
                              background: "#fef2f2",
                              borderRadius: "12px",
                              color: "#ef4444",
                              fontWeight: "600",
                              fontSize: "0.9rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.2s"
                           }}
                           onMouseOver={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
                           onMouseOut={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
                        >
                           <FiLogOut style={{ marginRight: "8px" }} /> Logout
                        </button>
                     </div>
                  </>
               ) : (
                  <div style={{ textAlign: "left", animation: "fadeIn 0.3s ease-in-out" }}>
                     <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                        <h5 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700", color: "#1e293b" }}>Change Password</h5>
                        <button
                           type="button"
                           onClick={() => setShowPasswordChange(false)}
                           style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: "0.85rem", fontWeight: "500" }}
                        >
                           Cancel
                        </button>
                     </div>
                     
                     <FormikProvider value={formik}>
                        <Form>
                           <div style={{ marginBottom: "16px" }}>
                              <CustomInput
                                 label="New Password"
                                 name="password"
                                 id="profile_password"
                                 require={true}
                                 inputType="password"
                                 placeholder="••••••••"
                                 {...formik.getFieldProps("password")}
                                 errors={formik.errors.password && formik.touched.password}
                                 message={formik.errors.password}
                              />
                           </div>
                           <div style={{ marginBottom: "20px" }}>
                              <CustomInput
                                 label="Confirm Password"
                                 name="password_confirmation"
                                 id="profile_c_password"
                                 require={true}
                                 inputType="password"
                                 placeholder="••••••••"
                                 {...formik.getFieldProps("password_confirmation")}
                                 errors={formik.errors.password_confirmation && formik.touched.password_confirmation}
                                 message={formik.errors.password_confirmation}
                              />
                           </div>
                           
                           <button
                              type="submit"
                              disabled={loading}
                              style={{
                                 width: "100%",
                                 padding: "14px 0",
                                 border: "none",
                                 background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                 borderRadius: "12px",
                                 color: "#fff",
                                 fontWeight: "600",
                                 fontSize: "0.95rem",
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "center",
                                 cursor: loading ? "not-allowed" : "pointer",
                                 boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                                 transition: "all 0.2s"
                              }}
                              onMouseOver={(e) => { if(!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
                              onMouseOut={(e) => { if(!loading) e.currentTarget.style.transform = "translateY(0)"; }}
                           >
                              {loading ? "Saving..." : <><FiSave style={{ marginRight: "8px" }} /> Update Password</>}
                           </button>
                        </Form>
                     </FormikProvider>
                  </div>
               )}
            </div>
         </div>

         {/* Beautiful Confirmation Modal */}
         <EmptyModal
            showModal={showConfirmModal}
            handleCloseModal={() => setShowConfirmModal(false)}
            modalSize="modal-md"
         >
            <div className="d-flex flex-column align-items-center text-center p-4">
               <div style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "#fef2f2",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px"
               }}>
                  <FiLogOut size={28} />
               </div>
               
               <h4 className="font-weight-bold mb-3" style={{ color: "#1e293b" }}>Automatic Logout</h4>
               <p style={{ color: "#64748b", fontSize: "1.05rem", lineHeight: "1.5", marginBottom: "30px" }}>
                  Changing your password will automatically log you out from your current session for security reasons. 
                  <br /><br />
                  Are you sure you want to proceed?
               </p>
               
               <div className="d-flex w-100 justify-content-center" style={{ gap: "15px" }}>
                  <button
                     className="btn btn-light"
                     style={{ width: "130px", fontWeight: "600", color: "#64748b", background: "#f1f5f9", border: "none" }}
                     onClick={() => setShowConfirmModal(false)}
                  >
                     Cancel
                  </button>
                  <button
                     className="btn btn-primary shadow-sm"
                     style={{ width: "130px", fontWeight: "600", background: "#4f46e5", border: "none" }}
                     onClick={executePasswordChange}
                     disabled={loading}
                  >
                     {loading ? "Updating..." : "Yes, Proceed"}
                  </button>
               </div>
            </div>
         </EmptyModal>
      </div>
   );
};

export default UserProfileModal;
